import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { scrollToElement, highlightDivContents } from 'lib/common';
import { LoadingIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { useEditorContext } from 'components/editor';
import { EditorHeader } from 'components/editor/header';
import { EditorBlocks, EditorTitle, EditorTransferBlocks } from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';

interface Props {
  translationProgressData: GetCourseTranslationProgressRes;
  updateTranslationProgressData: () => void;
}

const EditorContent = ({
  translationProgressData,
  updateTranslationProgressData
}: Props) => {
  const {
    editorBlocks,
    editorBlocksOrder,
    editorChapterTitle,
    editorChapterTitleLocales,
    editorCurrentChapter,
    editorIsConnecting,
    editorConnectionError,
    editorIsDisabled,
    editorIsFetching,
    editorPreviewType
  } = useEditorContext();

  const { messages, theme } = useReduxState();

  const [downloadableContent, setDownloadableContent] = useState<number[]>([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!editorCurrentChapter?.id) return;

    const content = [];
    editorBlocksOrder.forEach((blockId) => {
      if (['image', 'video'].includes(editorBlocks[blockId].type)) {
        content.push(blockId);
      }
    });

    setDownloadableContent(content);
    setIsReady(!content.length);
  }, [editorCurrentChapter]);

  useEffect(() => {
    if (isReady) {
      const blockId = Number(
        sessionStorage.getItem('editorQueryRoutingBlockId')
      );
      const element = document.querySelector(
        `.block[data-block-id="${blockId}"]`
      ) as HTMLElement;

      if (blockId !== -1 && element) {
        scrollToElement({ element, offset: 200 });
        highlightDivContents({ element });
        sessionStorage.removeItem('editorQueryRoutingBlockId');
      }
    }
  }, [isReady]);

  useEffect(() => {
    window.addEventListener('editor_image_loaded', contentHasLoaded);
    window.addEventListener('editor_video_loaded', contentHasLoaded);
    return () => {
      window.removeEventListener('editor_image_loaded', contentHasLoaded);
      window.removeEventListener('editor_video_loaded', contentHasLoaded);
    };
  }, []);

  const contentHasLoaded = (ev: CustomEvent<{ blockId: number }>) => {
    const { blockId } = ev.detail;

    const remaining = downloadableContent.filter((c) => Number(c) !== blockId);
    setDownloadableContent(remaining);
    if (!remaining.length) setIsReady(true);
  };

  return (
    <div className="content">
      {(editorIsConnecting || editorIsFetching) &&
      !editorIsDisabled &&
      !editorPreviewType ? (
        <div className="loading">
          <Box>
            <LoadingIndicator />
          </Box>
        </div>
      ) : editorConnectionError ? (
        <div className="connection-error">
          {editorConnectionError === 'unauthorized'
            ? messages.errorForbidden
            : messages.commonError}
        </div>
      ) : (
        <>
          <EditorHeader
            translationProgressData={translationProgressData}
            updateTranslationProgressData={updateTranslationProgressData}
          />

          <div className="inner-wrapper">
            {editorIsDisabled && !editorPreviewType ? (
              <div className="disabled-loading">
                <LoadingIndicator />
              </div>
            ) : null}

            <div className="inner">
              {editorIsDisabled && !editorPreviewType ? (
                <div className="disabled" />
              ) : null}

              <EditorTitle
                title={editorChapterTitle}
                titleLocales={editorChapterTitleLocales}
              />

              <EditorBlocks
                chapterId={editorCurrentChapter.id}
                isReady={isReady}
              />
            </div>
          </div>

          <EditorTransferBlocks />
        </>
      )}

      <style jsx>{`
        .content {
          position: relative;
          top: 10px;
        }
        .loading {
          padding-top: 10px;
        }
        .inner-wrapper {
          background-color: white;
          border-radius: ${`0 0 ${theme.cardBorderRadius} ${theme.cardBorderRadius}`};
          box-shadow: ${theme.cardBoxShadow};
        }
        .inner {
          color: ${theme.textColor};
          font-size: 1.6rem;
          margin: 0 auto;
          max-width: 760px;
          padding: 10px 15px 20px;
          position: relative;
        }
        .disabled {
          background-color: rgba(255, 255, 255, 0.7);
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1;
        }
        .disabled-loading {
          height: 0;
          opacity: 0.3;
          position: sticky;
          text-align: center;
          top: 85px;
          width: 100%;
          z-index: 10;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .content {
            top: -10px;
          }
        }
      `}</style>
    </div>
  );
};

export default EditorContent;
